import React, { useRef, useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lock, CheckCircle, BookOpen, Sparkles, Lightbulb, Heart, Users,
  Gavel, Landmark, TrendingUp, Flag, BookText, Crown, Play, GraduationCap, X
} from 'lucide-react';
import { getTopicColors } from '../utils/topicColors';
import { FLASHCARD_SETS } from '../catalog/flashcards';
import { useDarkMode } from '../contexts/DarkModeContext';

// Topic icon mapping using Lucide icons
const getTopicIcon = (topicId: string) => {
  const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
    'filosofie': Lightbulb,
    'psychologie': Heart,
    'sociologie': Users,
    'pravo': Gavel,
    'politologie': Landmark,
    'ekonomie': TrendingUp,
    'evropska-unie': Flag,
    'moderni-historie': BookText
  };
  return iconMap[topicId] || BookOpen;
};

// Extended node interface to support all features
interface StudyPathNode {
  id: string;
  title: string;
  topicId?: string;
  type: 'topic' | 'chapter' | 'flashcard';
  description?: string;
  isLocked?: boolean;
  isCompleted?: boolean;
  current?: boolean;
  progress?: { completed: number; total: number };
  flashcardCount?: number;
}

interface PathSegment {
  path: string;
  startNodeIndex: number;
  endNodeIndex: number;
}

interface StudyPathMapProps {
  chapters: Array<{
    id: string;
    title: string;
    topic?: string;
    type?: 'topic' | 'chapter';
    completed?: boolean;
    current?: boolean;
    progress?: number;
    isLocked?: boolean;
    description?: string;
  }>;
  onChapterClick?: (id: string) => void;
  onTopicClick?: (topicId: string) => void;
  onFlashcardClick?: (topicId: string) => void;
  onUpgradePro?: () => void;
  hasPlus?: boolean;
}

const StudyPathMap: React.FC<StudyPathMapProps> = ({
  chapters,
  onChapterClick,
  onTopicClick,
  onFlashcardClick,
  onUpgradePro,
  hasPlus = false
}) => {
  const { isDarkMode } = useDarkMode();
  const containerRef = useRef<HTMLDivElement>(null);
  const [nodePositions, setNodePositions] = useState<{ x: number, y: number }[]>([]);
  const [pathSegments, setPathSegments] = useState<PathSegment[]>([]);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [selectedNode, setSelectedNode] = useState<StudyPathNode | null>(null);

  // Constants for layout - DO NOT CHANGE THESE
  const NODE_SPACING = 150;
  const AMPLITUDE_PERCENT = 0.35;
  const NODES_PER_WAVE = 6;

  // Process chapters to insert Topic Nodes and Flashcard Nodes
  const displayNodes = useMemo((): StudyPathNode[] => {
    const nodes: StudyPathNode[] = [];
    let lastTopic = "";

    chapters.forEach((chapter, index) => {
      // If we encounter a new topic, insert a Topic Node header
      if (chapter.topic && chapter.topic !== lastTopic) {
        lastTopic = chapter.topic;

        const topicChapters = chapters.filter(c => c.topic === chapter.topic);
        const allCompleted = topicChapters.every(c => c.completed);
        const anyCurrent = topicChapters.some(c => c.current);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const progressSum = topicChapters.reduce((acc, c) => acc + (c.progress || 0), 0);

        nodes.push({
          id: `topic-${chapter.topic}`,
          title: chapter.topic,
          type: 'topic',
          topicId: chapter.topic,
          isCompleted: allCompleted,
          current: anyCurrent && !allCompleted,
          progress: {
            completed: topicChapters.filter(c => c.completed).length,
            total: topicChapters.length
          }
        });
      }

      // Add the actual chapter node
      nodes.push({
        id: chapter.id,
        title: chapter.title,
        type: chapter.type === 'topic' ? 'topic' : 'chapter',
        topicId: chapter.topic,
        isCompleted: chapter.completed || false,
        current: chapter.current || false,
        isLocked: chapter.isLocked || false,
        description: chapter.description
      });

      // Check if this is the last chapter of the topic
      const isLastChapterOfTopic = index === chapters.length - 1 ||
        (chapters[index + 1] && chapters[index + 1].topic !== chapter.topic);

      // If it's the last chapter and there's a flashcard set for this topic, add it
      if (isLastChapterOfTopic && chapter.topic && FLASHCARD_SETS[chapter.topic]) {
        const flashcardSet = FLASHCARD_SETS[chapter.topic];
        nodes.push({
          id: `flashcard-${chapter.topic}`,
          title: flashcardSet.title,
          type: 'flashcard',
          topicId: chapter.topic,
          isLocked: flashcardSet.access === 'pro' && !hasPlus,
          flashcardCount: 0
        });
      }
    });

    return nodes;
  }, [chapters, hasPlus]);

  // Calculate dynamic height
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const contentHeight = Math.max(600, displayNodes.length * NODE_SPACING + 200);

        setDimensions({
          width: containerWidth,
          height: contentHeight
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [displayNodes.length]);

  // Generate path segments between nodes
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const generatePathSegments = useMemo(() => {
    const { width, height } = dimensions;
    const segments: PathSegment[] = [];

    const amplitude = width * AMPLITUDE_PERCENT;
    const centerX = width / 2;
    const waveLength = NODES_PER_WAVE * NODE_SPACING;
    const frequency = (2 * Math.PI) / waveLength;

    const startPadding = 30;
    const usableHeight = height - 60;

    // Generate points for each node position
    const points: { x: number, y: number }[] = [];
    for (let i = 0; i < displayNodes.length; i++) {
      const y = startPadding + (i / Math.max(1, displayNodes.length - 1)) * usableHeight;
      const x = centerX + Math.cos(y * frequency) * amplitude;
      points.push({ x, y });
    }

    // Create segments between consecutive points
    for (let i = 0; i < points.length - 1; i++) {
      const start = points[i];
      const end = points[i + 1];

      // Generate smooth curve between points using sine wave
      const segmentPoints: string[] = [];
      const steps = 20; // Number of points for smoothness

      for (let step = 0; step <= steps; step++) {
        const t = step / steps;
        const y = start.y + (end.y - start.y) * t;
        const x = centerX + Math.cos(y * frequency) * amplitude;

        if (step === 0) {
          segmentPoints.push(`M ${x} ${y}`);
        } else {
          segmentPoints.push(`L ${x} ${y}`);
        }
      }

      segments.push({
        path: segmentPoints.join(' '),
        startNodeIndex: i,
        endNodeIndex: i + 1
      });
    }

    setNodePositions(points);
    setPathSegments(segments);

    return segments;
  }, [dimensions, displayNodes.length]);

  // generatePathSegments is evaluated and returns segments automatically

  // Handle node click - only open modal, don't navigate directly
  const handleNodeClick = (node: StudyPathNode) => {
    setSelectedNode(node);
  };

  // Get color for a path segment
  const getSegmentColor = (segment: PathSegment): string => {
    const startNode = displayNodes[segment.startNodeIndex];
    if (!startNode) return '#4B5563';

    // A segment is active if its start node is completed, OR if it's the topic node itself
    const isActive = startNode.isCompleted || startNode.type === 'topic';

    // If starting node is completed (or it's the topic header), use its topic color
    if (isActive && startNode.topicId) {
      const colors = getTopicColors(startNode.topicId);
      return colors?.primary || '#4B5563';
    }

    // Otherwise gray
    return '#4B5563';
  };

  // Get icon for node
  const getNodeIcon = (node: StudyPathNode) => {
    if (node.type === 'topic' && node.topicId) {
      const Icon = getTopicIcon(node.topicId);
      return <Icon className="w-5 h-5 text-white" />;
    }
    if (node.type === 'flashcard') {
      return <Sparkles className="w-5 h-5 text-yellow-400" />;
    }
    if (node.isCompleted) {
      return <CheckCircle className="w-5 h-5 text-green-400" />;
    }
    if (node.isLocked) {
      return <Lock className="w-4 h-4 text-gray-400" />;
    }
    return <BookOpen className="w-4 h-4 text-gray-300" />;
  };

  return (
    <div ref={containerRef} className="w-full relative" style={{ height: dimensions.height }}>
      <svg
        width="100%"
        height={dimensions.height}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        className="absolute top-0 left-0"
        style={{ overflow: 'visible' }}
      >
        {/* Background gray path (shows incomplete sections) */}
        {pathSegments.map((segment, index) => (
          <motion.path
            key={`bg-${index}`}
            d={segment.path}
            stroke="#4B5563"
            strokeWidth="24"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.3 }}
            transition={{
              duration: 0.5,
              ease: "linear",
              delay: index * 0.5
            }}
          />
        ))}

        {/* Colored path segments (completed sections) */}
        {pathSegments.map((segment, index) => {
          const startNode = displayNodes[segment.startNodeIndex];
          // Determine if segment is active (completed chapters, or from topic head)
          const isActive = (startNode?.isCompleted || startNode?.type === 'topic') || false;
          const segmentColor = getSegmentColor(segment);

          return (
            <motion.path
              key={`color-${index}`}
              d={segment.path}
              stroke={segmentColor}
              strokeWidth="24"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{
                pathLength: isActive ? 1 : 0,
                opacity: isActive ? 1 : 0
              }}
              transition={{
                duration: 0.5,
                ease: "linear",
                delay: index * 0.5
              }}
            />
          );
        })}

        {/* Chapter/Topic nodes */}
        {nodePositions.map((pos, index) => {
          const node = displayNodes[index];
          if (!node) return null;

          const isTopic = node.type === 'topic';
          const isFlashcard = node.type === 'flashcard';
          const isCurrent = node.current;
          const colors = node.topicId ? getTopicColors(node.topicId) : null;

          // Visual hierarchy settings - TOPIC CIRCLES 35% BIGGER (15% + 20%)
          const nodeRadius = isTopic ? 53 : 28;
          const shadowRadius = isTopic ? 61 : 34;
          const strokeWidth = isTopic ? 4 : 2;

          // Color logic using topic colors
          let fillColor = colors?.primary || '#6366F1';
          let strokeColor = colors?.secondary || '#818CF8';
          let nodeOpacity = 1;

          if (node.isCompleted) {
            fillColor = '#10B981';
            strokeColor = '#34D399';
          } else if (isCurrent && !isTopic) {
            fillColor = '#6B7280';
            strokeColor = '#9CA3AF';
          } else if (!isTopic && !isFlashcard) {
            fillColor = '#6B7280';
            strokeColor = '#9CA3AF';
          }

          // Flashcards - gray like chapters
          if (isFlashcard) {
            fillColor = '#6B7280';
            strokeColor = '#9CA3AF';
          }

          return (
            <g
              key={node.id}
              transform={`translate(${pos.x}, ${pos.y})`}
              onClick={() => handleNodeClick(node)}
              className="cursor-pointer"
              style={{ cursor: 'pointer' }}
            >
              <motion.g
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 20,
                  delay: index * 0.5
                }}
              >
                {/* Shadow/background circle */}
                <circle
                  r={shadowRadius}
                  fill="#1F2937"
                  opacity="0.4"
                />

                {/* Outer border for all nodes */}
                <circle
                  r={nodeRadius + 3}
                  fill="none"
                  stroke={isDarkMode ? "#132227" : "#f8f9fa"}
                  strokeWidth="8"
                />

                {/* Main node circle */}
                <circle
                  r={nodeRadius}
                  fill={fillColor}
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  className="transition-all duration-300 hover:opacity-90"
                  opacity={nodeOpacity}
                />

                {/* Inner white ring for current chapter */}
                {isCurrent && (
                  <circle
                    r={nodeRadius - 8}
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    opacity="0.9"
                  />
                )}

                {/* Progress ring for topics */}
                {isTopic && node.progress && node.progress.total > 0 && (
                  <circle
                    r={nodeRadius + 6}
                    fill="none"
                    stroke="#F59E0B"
                    strokeWidth="4"
                    strokeDasharray={`${2 * Math.PI * (nodeRadius + 6) * (node.progress.completed / node.progress.total)} ${2 * Math.PI * (nodeRadius + 6)}`}
                    strokeLinecap="round"
                    transform="rotate(-90)"
                    opacity="0.9"
                  />
                )}

                {/* Lock overlay */}
                {node.isLocked && (
                  <>
                    <circle r={nodeRadius} fill="rgba(0,0,0,0.4)" />
                    <foreignObject x="-12" y="-12" width="24" height="24" style={{ pointerEvents: 'none' }}>
                      <div className="flex items-center justify-center w-full h-full">
                        <Lock className="w-5 h-5 text-white" />
                      </div>
                    </foreignObject>
                  </>
                )}

                {/* Icon */}
                {!node.isLocked && (
                  <foreignObject x="-12" y="-12" width="24" height="24" style={{ pointerEvents: 'none' }}>
                    <div className="flex items-center justify-center w-full h-full">
                      {getNodeIcon(node)}
                    </div>
                  </foreignObject>
                )}

                {/* Label */}
                <text
                  textAnchor="middle"
                  y={nodeRadius + 25}
                  fill={isTopic
                    ? (isDarkMode ? "#F3F4F6" : "#1F2937")
                    : (isDarkMode ? "#D1D5DB" : "#4B5563")
                  }
                  fontSize={isTopic ? 16 : 13}
                  fontWeight={isTopic ? '700' : '500'}
                  className="select-none pointer-events-none"
                  opacity={isTopic ? 1 : (isDarkMode ? 0.8 : 1)}
                >
                  {node.title.length > 20 ? node.title.substring(0, 18) + '...' : node.title}
                </text>

                {/* Progress text */}
                {isTopic && node.progress && node.progress.total > 0 && (
                  <text
                    textAnchor="middle"
                    y={nodeRadius + 42}
                    fill={isDarkMode ? "#9CA3AF" : "#6B7280"}
                    fontSize="11"
                    fontWeight="500"
                    className="pointer-events-none"
                  >
                    {node.progress.completed}/{node.progress.total}
                  </text>
                )}
              </motion.g>
            </g>
          );
        })}
      </svg>

      {/* Modal */}
      <AnimatePresence>
        {selectedNode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setSelectedNode(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className={`
                relative w-full max-w-md overflow-hidden rounded-3xl shadow-2xl
                ${selectedNode.type === 'topic' && selectedNode.topicId
                  ? `bg-gradient-to-br ${getTopicColors(selectedNode.topicId).gradient}`
                  : selectedNode.type === 'flashcard'
                    ? 'bg-gradient-to-br from-yellow-600 to-yellow-800'
                    : 'bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700'
                }
              `}
            >
              <button
                onClick={() => setSelectedNode(null)}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              <div className="p-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.1 }}
                  className={`
                    w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center shadow-xl
                    ${selectedNode.type === 'topic'
                      ? 'bg-white/20'
                      : selectedNode.type === 'flashcard'
                        ? 'bg-yellow-500/20'
                        : selectedNode.isCompleted
                          ? 'bg-green-500/20'
                          : 'bg-gray-700/50'
                    }
                  `}
                >
                  {selectedNode.type === 'topic' && selectedNode.topicId ? (
                    React.createElement(getTopicIcon(selectedNode.topicId), { className: "w-10 h-10 text-white" })
                  ) : selectedNode.type === 'flashcard' ? (
                    <Sparkles className="w-10 h-10 text-yellow-400" />
                  ) : selectedNode.isCompleted ? (
                    <CheckCircle className="w-10 h-10 text-green-400" />
                  ) : selectedNode.isLocked ? (
                    <Lock className="w-10 h-10 text-gray-400" />
                  ) : (
                    <BookOpen className="w-10 h-10 text-blue-400" />
                  )}
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-2xl font-bold text-white text-center mb-2"
                >
                  {selectedNode.title}
                </motion.h2>

                {selectedNode.description && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-purple-200 text-center mb-6"
                  >
                    {selectedNode.description}
                  </motion.p>
                )}

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.35 }}
                  className="flex justify-center mb-4"
                >
                  <span className={`
                    px-3 py-1 rounded-full text-sm font-medium
                    ${selectedNode.type === 'topic' ? 'bg-purple-500/30 text-purple-200' : ''}
                    ${selectedNode.type === 'chapter' ? 'bg-blue-500/30 text-blue-200' : ''}
                    ${selectedNode.type === 'flashcard' ? 'bg-yellow-500/30 text-yellow-200' : ''}
                  `}>
                    {selectedNode.type === 'topic' && 'Téma'}
                    {selectedNode.type === 'chapter' && 'Kapitola'}
                    {selectedNode.type === 'flashcard' && 'Flashcards'}
                  </span>
                </motion.div>

                {selectedNode.type === 'topic' && selectedNode.progress && selectedNode.progress.total > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mb-6"
                  >
                    <div className="flex justify-between text-sm text-purple-200 mb-2">
                      <span>Progress</span>
                      <span>{selectedNode.progress.completed}/{selectedNode.progress.total} kapitol</span>
                    </div>
                    <div className="h-3 bg-black/30 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{
                          width: `${(selectedNode.progress.completed / selectedNode.progress.total) * 100}%`
                        }}
                        transition={{ duration: 1, ease: "easeOut", delay: 0.5 }}
                        className="h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full"
                      />
                    </div>
                    <p className="text-center text-purple-300 text-sm mt-2">
                      {Math.round((selectedNode.progress.completed / selectedNode.progress.total) * 100)}% dokončeno
                    </p>
                  </motion.div>
                )}

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.45 }}
                  className="flex justify-center gap-3 mb-6"
                >
                  {selectedNode.isCompleted && (
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
                      Dokončeno
                    </span>
                  )}
                  {selectedNode.isLocked && (
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-medium flex items-center gap-1">
                      <Crown className="w-4 h-4" />
                      PREMIUM obsah
                    </span>
                  )}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="space-y-3"
                >
                  {selectedNode.type === 'chapter' && !selectedNode.isLocked && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        if (onChapterClick) onChapterClick(selectedNode.id);
                        setSelectedNode(null);
                      }}
                      className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all"
                    >
                      <Play className="w-5 h-5" />
                      Začít kapitolu
                    </motion.button>
                  )}

                  {selectedNode.type === 'topic' && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        if (onTopicClick && selectedNode.topicId) onTopicClick(selectedNode.topicId);
                        setSelectedNode(null);
                      }}
                      className="w-full py-4 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all"
                    >
                      <GraduationCap className="w-5 h-5" />
                      Zobrazit všechny kapitoly
                    </motion.button>
                  )}

                  {selectedNode.type === 'flashcard' && !selectedNode.isLocked && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        if (onFlashcardClick && selectedNode.topicId) onFlashcardClick(selectedNode.topicId);
                        setSelectedNode(null);
                      }}
                      className="w-full py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all"
                    >
                      <Sparkles className="w-5 h-5" />
                      Studovat flashcards
                    </motion.button>
                  )}

                  {selectedNode.isLocked && onUpgradePro && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        onUpgradePro();
                        setSelectedNode(null);
                      }}
                      className="w-full py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all"
                    >
                      <Crown className="w-5 h-5" />
                      Upgradovat na PREMIUM
                    </motion.button>
                  )}

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedNode(null)}
                    className="w-full py-3 bg-transparent border border-white/20 text-white/70 hover:text-white hover:border-white/40 font-medium rounded-xl transition-all"
                  >
                    Zavřít
                  </motion.button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StudyPathMap;
